import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { usePortfolios } from '@/hooks/usePortfolios';
import Card from '@/components/UI/Card';
import Button from '@/components/UI/Button';
import type { PortfolioAdmissionComparisonPreview, PortfolioAdmissionCopyPreview } from '@einhaus/project314-types';

const inputClass = 'w-full rounded border border-zinc-700 bg-zinc-900 p-2';
const phrase = 'Copy settings; keep trading off';
export default function PortfolioAdmissionCard({ portfolioId }: { portfolioId: string | undefined }) {
    const id = Number(portfolioId);
    const [caseId, setCaseId] = useState(0);
    const [pages, setPages] = useState<number[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [nativeMode, setNativeMode] = useState(false);
    const [notes, setNotes] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [comparison, setComparison] = useState<PortfolioAdmissionComparisonPreview | null>(null);
    const [copy, setCopy] = useState<PortfolioAdmissionCopyPreview | null>(null);
    const { portfolios } = usePortfolios();
    const listing = useAppQuery({
        ...trpc.portfolioAdmission.list.queryOptions({ portfolioId: id, afterId: pages.at(-1) }),
        enabled: id > 0
    });
    const detail = useAppQuery({ ...trpc.portfolioAdmission.get.queryOptions({ caseId }), enabled: caseId > 0 });
    const admission = detail.data?.admission;
    const expectedRevision = admission?.revision ?? 0;
    const audit = useAppQuery({ ...trpc.portfolioAdmission.audit.queryOptions({ caseId }), enabled: false });
    const evidence = useAppQuery({ ...trpc.portfolioAdmission.executionEvidence.queryOptions({ caseId, days: 30 }), enabled: false });
    const comparisonQuery = useAppQuery({
        ...trpc.portfolioAdmission.previewComparison.queryOptions({ caseId, expectedRevision }),
        enabled: false
    });
    const copyQuery = useAppQuery({ ...trpc.portfolioAdmission.previewCopy.queryOptions({ caseId, expectedRevision }), enabled: false });
    const create = useMutation(trpc.portfolioAdmission.create.mutationOptions());
    const freeze = useMutation(trpc.portfolioAdmission.freeze.mutationOptions());
    const start = useMutation(trpc.portfolioAdmission.startComparison.mutationOptions());
    const review = useMutation(trpc.portfolioAdmission.review.mutationOptions());
    const copySettings = useMutation(trpc.portfolioAdmission.copySettings.mutationOptions());
    function select(value: number) {
        setCaseId(value);
        setComparison(null);
        setCopy(null);
        setConfirmation('');
        setNotes('');
    }
    async function act(work: () => Promise<unknown>) {
        setBusy(true);
        setError('');
        try {
            await work();
            await listing.refetch();
            if (caseId) await detail.refetch();
        } catch (failure) {
            setError(failure instanceof Error ? failure.message : 'Proposal action failed');
        } finally {
            setBusy(false);
        }
    }
    if (!id) return null;
    return (
        <Card title="Portfolio admission">
            <div className="space-y-4">
                <p>
                    Compare the whole bundle, review existing trading evidence, then copy settings to an inactive live portfolio. Trading
                    activation stays in the existing portfolio controls.
                </p>
                {(error || listing.error || detail.error) && (
                    <p role="alert" className="text-red-400">
                        {error || listing.error?.message || detail.error?.message}
                    </p>
                )}
                <select
                    aria-label="Admission proposal"
                    className={inputClass}
                    value={caseId}
                    disabled={busy}
                    onChange={(e) => select(Number(e.target.value))}
                >
                    <option value={0}>Select a proposal</option>
                    {caseId > 0 && admission && !listing.data?.some((item) => item.id === caseId) && (
                        <option value={caseId}>
                            {admission.name} — {admission.state}
                        </option>
                    )}
                    {listing.data?.map((item) => (
                        <option key={item.id} value={item.id}>
                            {item.name} — {item.state}
                        </option>
                    ))}
                </select>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="secondary"
                        disabled={busy || !pages.length}
                        onClick={() => {
                            setPages((p) => p.slice(0, -1));
                            select(0);
                        }}
                    >
                        Previous proposals
                    </Button>
                    <Button
                        variant="secondary"
                        disabled={busy || (listing.data?.length ?? 0) < 50}
                        onClick={() => {
                            const last = listing.data?.at(-1)?.id;
                            if (last) {
                                setPages((p) => [...p, last]);
                                select(0);
                            }
                        }}
                    >
                        More proposals
                    </Button>
                    <Button variant="secondary" disabled={busy} onClick={() => setShowCreate(!showCreate)}>
                        New proposal
                    </Button>
                </div>
                {showCreate && (
                    <form
                        className="space-y-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                            const form = new FormData(e.currentTarget);
                            const role = nativeMode ? 'return' : form.get('role');
                            if (role !== 'return' && role !== 'diversifier' && role !== 'hedge') return;
                            const number = (key: string) => Number(form.get(key));
                            const date = (key: string, end: boolean) => {
                                const value = String(form.get(key));
                                return nativeMode
                                    ? `${value.replace('T', ' ')}${value.length === 16 ? ':00' : ''}`
                                    : `${value} ${end ? '23:59:59' : '00:00:00'}`;
                            };
                            void act(async () => {
                                const created = await create.mutateAsync({
                                    stagingPortfolioId: number('stagingPortfolioId'),
                                    livePortfolioId: number('livePortfolioId'),
                                    name: String(form.get('name')),
                                    rationale: String(form.get('rationale')),
                                    role,
                                    startDateTime: date('startDate', false),
                                    endDateTime: date('endDate', true),
                                    trainingEndDateTime: date('trainingEnd', true),
                                    researchExposedThrough: date('exposedThrough', true),
                                    startingCapital: nativeMode ? 5000 : number('startingCapital'),
                                    minimumSharpeImprovement: number('minimumSharpeImprovement'),
                                    minimumReturnChange: number('minimumReturnChange'),
                                    maximumDrawdownIncrease: number('maximumDrawdownIncrease'),
                                    minimumUnseenDays: number('minimumUnseenDays'),
                                    stressStockPerShare: nativeMode ? 0 : number('stressStockPerShare'),
                                    stressStockMinimum: nativeMode ? 0 : number('stressStockMinimum'),
                                    stressOptionPerContract: nativeMode ? 0 : number('stressOptionPerContract'),
                                    controlSizeFraction:
                                        nativeMode || form.get('controlSizeFraction') === '' ? null : number('controlSizeFraction'),
                                    futuresBacktestConfigurationId: nativeMode ? number('futuresBacktestConfigurationId') : null,
                                    stressFuturesBacktestConfigurationId: nativeMode ? number('stressFuturesBacktestConfigurationId') : null
                                });
                                select(created);
                                setShowCreate(false);
                            });
                        }}
                    >
                        <p>
                            Choose existing portfolios. Only enabled subscriptions form the proposed bundle; the staging portfolio itself
                            can remain inactive.
                        </p>
                        <label className="block">
                            <input type="checkbox" checked={nativeMode} onChange={(event) => setNativeMode(event.target.checked)} /> Native
                            one-contract MES comparison
                        </label>
                        {nativeMode && (
                            <div className="space-y-2 rounded border border-zinc-700 p-3">
                                <p>
                                    Use a SPY-only, fixed five-minute native recipe and an empty cash incumbent. Equal capital stays $5,000.
                                    Select immutable ordinary/stressed configurations with the same replay, reserve, contract selection and
                                    slippage; only the native per-side fee increases.
                                </p>
                                {(['futuresBacktestConfigurationId', 'stressFuturesBacktestConfigurationId'] as const).map(
                                    (name, index) => (
                                        <label className="block" key={name}>
                                            {index ? 'Higher-fee native configuration ID' : 'Ordinary native configuration ID'}
                                            <input className={inputClass} name={name} type="number" min="1" step="1" required />
                                        </label>
                                    )
                                )}
                            </div>
                        )}
                        {(['stagingPortfolioId', 'livePortfolioId'] as const).map((field) => (
                            <label className="block" key={field}>
                                {field === 'stagingPortfolioId' ? 'Paper staging portfolio' : 'Live destination'}
                                <select className={inputClass} name={field} required defaultValue="">
                                    <option value="">Choose a portfolio</option>
                                    {portfolios
                                        .filter((p) => p.type === (field === 'stagingPortfolioId' ? 'paper' : 'live'))
                                        .map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                </select>
                            </label>
                        ))}
                        <label className="block">
                            Proposal name
                            <input name="name" className={inputClass} required maxLength={200} />
                        </label>
                        <label className="block">
                            Purpose
                            {nativeMode ? (
                                <p>Return · explicit cash incumbent</p>
                            ) : (
                                <select name="role" className={inputClass}>
                                    <option value="return">Return</option>
                                    <option value="diversifier">Diversification</option>
                                    <option value="hedge">Hedge</option>
                                </select>
                            )}
                        </label>
                        <label className="block">
                            Why this bundle
                            <textarea name="rationale" className={inputClass} required minLength={20} maxLength={2000} />
                        </label>
                        <div className="grid gap-3 md:grid-cols-2">
                            {[
                                ['startDate', 'Backtest starts'],
                                ['endDate', 'Backtest ends'],
                                ['trainingEnd', 'Training ends'],
                                ['exposedThrough', 'Latest data examined in any research']
                            ].map(([name, label]) => (
                                <label key={name}>
                                    {label}
                                    <input
                                        type={nativeMode ? 'datetime-local' : 'date'}
                                        step={nativeMode ? 60 : undefined}
                                        name={name}
                                        className={inputClass}
                                        required
                                    />
                                    {nativeMode && <small>Eastern; match the native configuration exactly. The end is exclusive.</small>}
                                </label>
                            ))}
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                            {[
                                ['startingCapital', 'Equal starting capital', 0],
                                ['minimumSharpeImprovement', 'Minimum Sharpe improvement', 0],
                                ['minimumReturnChange', 'Minimum return change (percentage points)', -100],
                                ['maximumDrawdownIncrease', 'Maximum drawdown increase (percentage points)', 0],
                                ['minimumUnseenDays', 'Minimum unseen days', 30],
                                ['stressStockPerShare', 'Adverse stock commission per share', 0],
                                ['stressStockMinimum', 'Adverse stock minimum commission', 0],
                                ['stressOptionPerContract', 'Adverse commission per option contract', 0.65]
                            ]
                                .filter(([name]) => !nativeMode || !String(name).startsWith('stress'))
                                .map(([name, label, min]) => (
                                    <label key={String(name)}>
                                        {label}
                                        <input
                                            key={`${String(name)}-${nativeMode}`}
                                            type="number"
                                            step="any"
                                            min={Number(min)}
                                            name={String(name)}
                                            required
                                            className={inputClass}
                                            {...(nativeMode && name === 'startingCapital' ? { value: 5000, readOnly: true } : {})}
                                        />
                                    </label>
                                ))}
                        </div>
                        {!nativeMode && (
                            <label className="block">
                                Reduced-incumbent sizing fraction (required for hedge/diversifier)
                                <input type="number" name="controlSizeFraction" step="any" min={0.001} max={0.999} className={inputClass} />
                            </label>
                        )}
                        <Button type="submit" disabled={busy}>
                            Save proposal
                        </Button>
                    </form>
                )}
                {admission && (
                    <>
                        <p>
                            {admission.name} · {admission.state} · Staging #{admission.stagingPortfolioId} → Live #
                            {admission.livePortfolioId}
                        </p>
                        <p>{admission.rationale}</p>
                        {admission.futuresBacktestConfigurationId != null && ['reviewed', 'copied'].includes(admission.state) && (
                            <a className="underline" href={`/tools/futuresPaper?caseId=${admission.id}#strategy-admission`}>
                                Prepare inactive native paper enrollment
                            </a>
                        )}
                        {detail.data?.configurationIssue && (
                            <p role="alert" className="text-amber-400">
                                {detail.data.configurationIssue}
                            </p>
                        )}
                        {admission.state === 'draft' && (
                            <Button disabled={busy} onClick={() => void act(() => freeze.mutateAsync({ caseId, expectedRevision }))}>
                                Freeze comparison settings
                            </Button>
                        )}
                        {admission.state === 'frozen' && (
                            <Button
                                disabled={busy}
                                onClick={() =>
                                    void act(async () => {
                                        const result = await comparisonQuery.refetch();
                                        if (result.error) throw new Error(result.error.message);
                                        setComparison(result.data ?? null);
                                    })
                                }
                            >
                                Preview comparison
                            </Button>
                        )}
                        {comparison && (
                            <div className="space-y-2">
                                <p>
                                    {comparison.arms.length} comparisons · ${comparison.startingCapital} each · {comparison.startDateTime}{' '}
                                    to {comparison.endDateTime}
                                </p>
                                <p>
                                    Arms: {comparison.arms.map((a) => a.arm + (a.cashControl ? ' (cash control)' : '')).join(', ')}. Reduced
                                    size: {comparison.controlSizeFraction ?? 'none'}.
                                </p>
                                {comparison.nativeConfigurations ? (
                                    <p>
                                        Native configurations #{comparison.nativeConfigurations.normalId} / #
                                        {comparison.nativeConfigurations.stressId}. Per-side fee $
                                        {(Number(comparison.nativeConfigurations.normalFeePerContractSideRaw) / 1e9).toFixed(2)} → $
                                        {(Number(comparison.nativeConfigurations.stressFeePerContractSideRaw) / 1e9).toFixed(2)}; reserve $
                                        {(Number(comparison.nativeConfigurations.researchReservePerContractRaw) / 1e9).toFixed(2)} per
                                        contract.
                                    </p>
                                ) : (
                                    <p>
                                        Adverse commissions: ${comparison.adverseCosts.stockPerShare}/share, $
                                        {comparison.adverseCosts.stockMinimum} stock minimum, ${comparison.adverseCosts.optionPerContract}
                                        /option contract.
                                    </p>
                                )}
                                <Button
                                    disabled={busy}
                                    onClick={() =>
                                        void act(async () => {
                                            await start.mutateAsync({
                                                caseId: comparison.caseId,
                                                expectedRevision: comparison.revision,
                                                previewHash: comparison.previewHash
                                            });
                                            setComparison(null);
                                        })
                                    }
                                >
                                    Run this comparison
                                </Button>
                            </div>
                        )}
                        <ul>
                            {detail.data?.runs.map((run) => (
                                <li key={run.id}>
                                    {run.arm}: {run.state} {run.errorMessage}{' '}
                                    {run.backtestId && (
                                        <a className="underline" href={`/user/portfolioBacktestStats?id=${run.backtestId}`}>
                                            Backtest #{run.backtestId}
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                        {admission.state !== 'draft' && (
                            <Button
                                variant="secondary"
                                disabled={busy}
                                onClick={() =>
                                    void act(async () => {
                                        const result = await audit.refetch();
                                        if (result.error) throw new Error(result.error.message);
                                    })
                                }
                            >
                                Review historical results
                            </Button>
                        )}
                        {audit.data && (
                            <div>
                                <p>Historical result: {audit.data.outcome}</p>
                                <ul>
                                    {audit.data.findings.map((f) => (
                                        <li key={f.code}>
                                            {f.outcome}: {f.message}
                                            {f.measuredValue !== null ? ` (${f.measuredValue}; limit ${f.allowedValue})` : ''}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <Button
                            variant="secondary"
                            disabled={busy}
                            onClick={() =>
                                void act(async () => {
                                    const result = await evidence.refetch();
                                    if (result.error) throw new Error(result.error.message);
                                })
                            }
                        >
                            Inspect last 30 days of staging evidence
                        </Button>
                        {evidence.data && (
                            <div>
                                <p>
                                    {evidence.data.fills.length} retained fills shown{evidence.data.truncated ? ' (truncated)' : ''}.
                                    Accounting mismatches: {evidence.data.accountingMismatches.length}; missing order IDs:{' '}
                                    {evidence.data.missingOrderEvidence.length}.
                                </p>
                                <ul>
                                    {evidence.data.limitations.map((text) => (
                                        <li key={text}>{text}</li>
                                    ))}
                                </ul>
                                <details>
                                    <summary>Fill identities and configuration history</summary>
                                    <pre className="overflow-auto text-xs">
                                        {JSON.stringify(
                                            {
                                                fills: evidence.data.fills,
                                                subscriptionChanges: evidence.data.subscriptionChanges,
                                                governanceDecisions: evidence.data.governanceDecisions
                                            },
                                            null,
                                            2
                                        )}
                                    </pre>
                                </details>
                            </div>
                        )}
                        {admission.state === 'frozen' && (
                            <div className="space-y-2">
                                <label>
                                    Review notes: record audit evidence, unresolved gaps and the decision
                                    <textarea
                                        aria-label="Review notes"
                                        className={inputClass}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        maxLength={4000}
                                    />
                                </label>
                                <Button
                                    disabled={busy || notes.trim().length < 20 || audit.data?.outcome !== 'pass'}
                                    onClick={() =>
                                        void act(() => review.mutateAsync({ caseId, expectedRevision, decision: 'accept', notes }))
                                    }
                                >
                                    Record review for settings copy
                                </Button>
                                <Button
                                    variant="secondary"
                                    disabled={busy || notes.trim().length < 20}
                                    onClick={() =>
                                        void act(() => review.mutateAsync({ caseId, expectedRevision, decision: 'reject', notes }))
                                    }
                                >
                                    Reject proposal
                                </Button>
                            </div>
                        )}
                        {admission.reviewNotes && <p>Owner review: {admission.reviewNotes}</p>}
                        {admission.state === 'reviewed' && (
                            <Button
                                disabled={busy}
                                onClick={() =>
                                    void act(async () => {
                                        const result = await copyQuery.refetch();
                                        if (result.error) throw new Error(result.error.message);
                                        setCopy(result.data ?? null);
                                        setConfirmation('');
                                    })
                                }
                            >
                                Preview settings copy
                            </Button>
                        )}
                        {copy && (
                            <div className="space-y-2">
                                <p>
                                    Live #{copy.livePortfolioId}: strategies {copy.previousStrategyIds.join(', ') || 'none'} →{' '}
                                    {copy.strategyIds.join(', ')}.
                                </p>
                                <p>
                                    Copy settings and governance only. The destination stays inactive; its cash and account stay unchanged.
                                </p>
                                {copy.blockers.map((text) => (
                                    <p role="alert" key={text}>
                                        {text}
                                    </p>
                                ))}
                                <label>
                                    Type “{phrase}”
                                    <input
                                        aria-label="Copy confirmation"
                                        className={inputClass}
                                        value={confirmation}
                                        onChange={(e) => setConfirmation(e.target.value)}
                                    />
                                </label>
                                <Button
                                    disabled={busy || copy.blockers.length > 0 || confirmation !== phrase}
                                    onClick={() =>
                                        void act(async () => {
                                            await copySettings.mutateAsync({
                                                caseId: copy.caseId,
                                                expectedRevision: copy.revision,
                                                previewHash: copy.previewHash,
                                                confirmation: phrase
                                            });
                                            setCopy(null);
                                        })
                                    }
                                >
                                    Copy settings without activating
                                </Button>
                            </div>
                        )}
                        {admission.state === 'copied' && <p>Settings copied. Trading was not activated.</p>}
                    </>
                )}
            </div>
        </Card>
    );
}
